import { z } from 'zod';

/**
 * The icon a settings **group** shows in the admin sidebar.
 *
 * It travels as registry metadata rather than living in an admin lookup table
 * for the reason W8 exists: a group comes into existence in the api's settings
 * registry, so an icon the admin owned would make "add a group" an admin code
 * change. It is a **closed union of semantic names** — never a lucide component
 * name and never a brand asset (U16) — so the same build serves every brand and
 * the admin is free to change which glyph draws "verification" without the api
 * knowing.
 *
 * The admin maps this union in a `switch` whose default arm is typed `never`,
 * so adding a member here fails `nx typecheck` until a glyph is chosen for it —
 * the same guard P10.3a put on `settingFieldFor`.
 */
export const SETTING_GROUP_ICONS = [
  'brand',
  'registration',
  'verification',
  'browse',
  'profile',
  'chat',
  'moderation',
  'trust',
  'billing',
  'courses',
  'legal',
  'notifications',
  'ai',
  'guardian',
  'meetings',
  'sessions',
  /** The fallback: what a group declared with no icon of its own renders. */
  'settings',
] as const;

export const SettingGroupIconSchema = z.enum(SETTING_GROUP_ICONS);

export type SettingGroupIcon = (typeof SETTING_GROUP_ICONS)[number];

/** Fail-closed narrowing for a name off the wire. */
export function isSettingGroupIcon(value: string): value is SettingGroupIcon {
  return SettingGroupIconSchema.safeParse(value).success;
}
